import type { ComponentProps } from 'react';
import { useCallback } from 'react';

import type { OptionType } from 'components/Select/Select.types';
import { SelectInput } from 'components/SelectInput/SelectInput';

import { Field } from './Field/Field';

type Props = {
  name: string;
  description?: string;
  options: readonly (OptionType & {
    selected?: boolean;
    hidden?: boolean;
    disabled?: boolean;
  })[];
  onUpdateValue?: (value?: OptionType['value']) => void;
  noMargin?: boolean;
  noWrap?: boolean;
};

export function SelectInputField({
  name,
  label,
  description,
  options,
  noMargin,
  noWrap,
  onUpdateValue,
  required,
  ...props
}: Props & ComponentProps<typeof SelectInput>) {
  const handleChange = useCallback(
    (updateValue: (v: unknown) => void) => (newValue: OptionType['value']) => {
      updateValue(newValue);

      if (onUpdateValue) {
        onUpdateValue(newValue);
      }
    },
    [onUpdateValue],
  );

  return (
    <Field
      inputProps={{ ...props, options }}
      name={name}
      label={label}
      description={description}
      noMargin={noMargin}
      noWrap={noWrap}
      renderInput={({ value, id, qaId, hasError, updateValue }) => (
        <SelectInput
          id={id}
          name={name}
          value={value ?? ''}
          options={options}
          hasError={hasError}
          data-qa-id={qaId}
          onChange={handleChange(updateValue)}
          required={required}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...props}
        />
      )}
    />
  );
}
